import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ContentChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '../base/BaseComponent';
import { LookupPipe } from '../pipes/lookup.pipe';
import { DsTableOptions } from './DsTableOptions';
import {
  DsTableColumnConfig,
  DsTableColumnDisplayPosition,
  DsTableColumnFormatEnum,
} from './TableColumnConfig';
import { DsTableColumnOverrideEnum, getClassParentNames } from './TableColumn';
import { DsTableActionEnum } from './DsTableActionEnum';
import { DsTableEmitHolder } from './EsListEmitHolder';

export const TABLE_COLUMNS_KEY = Symbol('tableColumns');

@Component({
  selector: 'ds-table',
  templateUrl: './ds-table.component.html',
  providers: [LookupPipe],
})
export class DsTableComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input()
  public options: DsTableOptions;

  @Input()
  public items: any[] = [];

  @Input()
  public modelType!: any;

  @Input()
  public enableEditing = true;

  public searchText: string = '';

  public isCollapsed: boolean[] = [];

  public columns: DsTableColumnConfig[] = [];
  public details: DsTableColumnConfig[] = [];

  @Input()
  public enablePagination = false;

  public paginationId: string;
  public currentPage: number = 1;
  public itemsPerPage: number = 4;

  @Output()
  edit: EventEmitter<DsTableEmitHolder<any>> = new EventEmitter();

  @Output()
  delete: EventEmitter<any> = new EventEmitter();

  public get isShowOptionsColumn(): boolean {
    return (
      this.enableEditing ||
      this.options?.showOptions ||
      this.options?.customOptions?.length > 0
    );
  }

  public get detailsColSpan(): number {
    if (this.isShowOptionsColumn) {
      return this.columns?.length + 1 ?? 0;
    }

    return this.columns?.length ?? 0;
  }

  @ContentChild('headerTemplate', { static: false })
  headerTemplate?: TemplateRef<any>;

  @ContentChild('rowContentTemplate', { static: false })
  rowContentTemplate?: TemplateRef<any>;

  @ContentChild('detailsTemplate', { static: false })
  detailsTemplate?: TemplateRef<any>;

  public get showCollapseDetailsButton(): boolean {
    return (
      (this.rowContentTemplate != undefined &&
        this.rowContentTemplate != null) ||
      (this.detailsTemplate != undefined && this.detailsTemplate != null) ||
      this.details?.length > 0
    );
  }

  constructor(
    private translate: TranslateService,
    private lookupPipe: LookupPipe
  ) {
    super(translate);

    this.paginationId = this.generateId();
  }

  public ngOnInit(): void {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.handleItemsChange();
    }
  }

  private handleItemsChange() {
    const cols: DsTableColumnConfig[] = this.getMetadata(this.modelType);

    this.columns = this.extractColumns(cols);
    this.details = this.extractDetails(cols);
  }

  private extractDetails(cols: DsTableColumnConfig[]): DsTableColumnConfig[] {
    const hiddenFields = cols
      .filter((c) => c.override == DsTableColumnOverrideEnum.Hide)
      .map((c) => c.property);

    const modelTypeParents = getClassParentNames(this.modelType);

    return cols
      .sort((a, b) => a.sequence - b.sequence)
      .filter((c: DsTableColumnConfig) => {
        return (
          c.modelType == this.modelType.name ||
          modelTypeParents.indexOf(c.modelType) >= 0
        );
      })
      .filter((c: DsTableColumnConfig) => {
        return (
          c.displayPosition == DsTableColumnDisplayPosition.Details &&
          hiddenFields.indexOf(c.property) == -1
        );
      });
  }

  private extractColumns(cols: DsTableColumnConfig[]): DsTableColumnConfig[] {
    if (cols) {
      const modelTypeParents = getClassParentNames(this.modelType);

      const hiddenFields = cols
        .filter(
          (c) =>
            c.override == DsTableColumnOverrideEnum.Hide &&
            (c.modelType == this.modelType.name ||
              modelTypeParents.indexOf(c.modelType) >= 0)
        )
        .map((c) => c.property);

      const header = cols
        .sort((a, b) => a.sequence - b.sequence)
        .filter((c: DsTableColumnConfig) => {
          return (
            c.modelType == this.modelType.name ||
            modelTypeParents.indexOf(c.modelType) >= 0
          );
        })
        .filter((c: DsTableColumnConfig) => {
          return (
            c.displayPosition == DsTableColumnDisplayPosition.Header &&
            hiddenFields.indexOf(c.property) == -1
          );
        });

      return header;
    }

    return [];
  }

  private getMetadata(modelType: any): DsTableColumnConfig[] {
    return Reflect.getMetadata(TABLE_COLUMNS_KEY, modelType) ?? [];
  }

  public emitEdit($event: any, item: any) {
    $event.stopPropagation();

    this.edit.emit({ item: item, action: DsTableActionEnum.Edit });
  }

  public emitDelete($event: any, item: any) {
    $event.stopPropagation();

    this.delete.emit({ item: item });
  }

  public updateSearchText($event: any) {
    this.searchText = $event.searchText;
  }

  public formatValue(value: any, col: DsTableColumnConfig): any {
    switch (col.format) {
      case DsTableColumnFormatEnum.Date:
        return new Date(value).toLocaleDateString();
      case DsTableColumnFormatEnum.Lookup:
        return this.lookupPipe.transform(value, this.lang);
      case DsTableColumnFormatEnum.Object:
        return value[col.displayProperty];
      default:
        return value || '';
    }
  }

  private generateId(): string {
    return 'id_' + Math.random().toString(36).substring(2, 10);
  }
}
